/**
 * Kolejka emisji trybu demo: `current`, `next` i pamięć ostatnich pozycji.
 *
 * Czysta logika bez audio i bez Reacta. Losowość i zegar są wstrzykiwane,
 * więc reguły rotacji da się testować deterministycznie. To celowo prosty
 * dobór, nie system rekomendacji - reguły ustawia się w `RADIO_ROTATION_RULES`.
 */

import { getTimeItemId } from './catalog';
import type { RadioItem } from './radio.types';
import type { HourlyScheduler } from './radioScheduler';

type Range = readonly [min: number, max: number];

export interface RotationRules {
  /** Utwór wokalny co tyle utworów (losowo z zakresu); `null` = bez reguły. */
  vocalEvery: Range | null;
  /** Jingiel lub identyfikator stacji co tyle utworów. */
  jingleEvery: Range | null;
  /** Przerywnik co tyle minut. */
  interludeEveryMinutes: Range | null;
  /** Ilu ostatnio granych pozycji unikać przy wyborze (o ile katalog pozwala). */
  avoidRecent: number;
  /** Nie grać dwa razy z rzędu tego samego wykonawcy (o ile katalog pozwala). */
  avoidSameArtist: boolean;
  /**
   * Muzyka nocna (pozycje z danym tagiem): poza godzinami `hours` jest pomijana,
   * a w ich trakcie wybierana `boost` razy chętniej. `null` = bez reguły.
   */
  night: { tag: string; hours: Range; boost: number } | null;
}

export interface RadioQueue {
  readonly current: RadioItem | null;
  readonly next: RadioItem | null;
  /** Przechodzi do następnej pozycji i zwraca nową bieżącą. */
  advance(): RadioItem | null;
}

export interface RadioQueueOptions {
  catalog: readonly RadioItem[];
  rules: RotationRules;
  /** Komunikaty pełnej godziny; bez schedulera kolejka ich nie wstawia. */
  scheduler?: HourlyScheduler;
  /** Identyfikatory ostatnio granych pozycji (np. z historii sesji), od najstarszej. */
  recentIds?: readonly string[];
  random?: () => number;
  now?: () => number;
}

const RECENT_MEMORY = 50;

export function createRadioQueue({
  catalog,
  rules,
  scheduler,
  recentIds = [],
  random = Math.random,
  now = Date.now,
}: RadioQueueOptions): RadioQueue {
  const ofType = (...types: RadioItem['type'][]): RadioItem[] =>
    catalog.filter((item) => types.includes(item.type));

  const music = ofType('music');
  const vocal = ofType('vocal');
  const jingles = ofType('jingle', 'station_id');
  const interludes = ofType('interlude');
  const timeItems = new Map(ofType('time').map((item) => [item.id, item]));

  const pickInRange = ([min, max]: Range): number => min + Math.floor(random() * (max - min + 1));

  const recent = [...recentIds];
  let lastArtist: string | undefined;
  let lastWasBreak = true;
  let tracksSinceVocal = 0;
  let tracksSinceJingle = 0;
  let vocalAfter = rules.vocalEvery ? pickInRange(rules.vocalEvery) : Infinity;
  let jingleAfter = rules.jingleEvery ? pickInRange(rules.jingleEvery) : Infinity;
  let interludeAt = rules.interludeEveryMinutes
    ? now() + pickInRange(rules.interludeEveryMinutes) * 60_000
    : Infinity;

  function remember(item: RadioItem): RadioItem {
    recent.push(item.id);
    if (recent.length > RECENT_MEMORY) recent.shift();
    return item;
  }

  /** Losuje z puli z pominięciem ostatnio granych pozycji i (jeśli się da) wykonawcy. */
  function pickFrom(
    pool: readonly RadioItem[],
    weightOf: (item: RadioItem) => number = (item) => item.weight ?? 1
  ): RadioItem {
    const poolIds = new Set(pool.map((item) => item.id));
    // Zawsze musi zostać przynajmniej jeden kandydat, stąd `pool.length - 1`.
    const blockedCount = Math.min(rules.avoidRecent, pool.length - 1);
    const blocked = new Set(
      blockedCount > 0 ? recent.filter((id) => poolIds.has(id)).slice(-blockedCount) : []
    );

    let candidates = pool.filter((item) => !blocked.has(item.id));
    if (rules.avoidSameArtist && lastArtist) {
      const otherArtists = candidates.filter((item) => item.artist !== lastArtist);
      if (otherArtists.length > 0) candidates = otherArtists;
    }

    const totalWeight = candidates.reduce((sum, item) => sum + weightOf(item), 0);
    let roll = random() * totalWeight;
    for (const item of candidates) {
      roll -= weightOf(item);
      if (roll < 0) return remember(item);
    }
    return remember(candidates[candidates.length - 1]);
  }

  function isNightTime(): boolean {
    if (!rules.night) return false;
    const [from, to] = rules.night.hours;
    const hour = new Date(now()).getHours();
    return from <= to ? hour >= from && hour < to : hour >= from || hour < to;
  }

  /** Wstawka między utworami (przerywnik albo jingiel), jeśli przyszła na nią pora. */
  function pickBreak(): RadioItem | null {
    if (lastWasBreak) return null;

    if (interludes.length > 0 && rules.interludeEveryMinutes && now() >= interludeAt) {
      interludeAt = now() + pickInRange(rules.interludeEveryMinutes) * 60_000;
      return pickFrom(interludes);
    }

    if (jingles.length > 0 && rules.jingleEvery && tracksSinceJingle >= jingleAfter) {
      tracksSinceJingle = 0;
      jingleAfter = pickInRange(rules.jingleEvery);
      return pickFrom(jingles);
    }

    return null;
  }

  function pickTrack(): RadioItem | null {
    const vocalDue = vocal.length > 0 && tracksSinceVocal >= vocalAfter;
    let pool = vocalDue || music.length === 0 ? vocal : music;
    if (pool.length === 0) return null;

    let weightOf: ((item: RadioItem) => number) | undefined;
    if (rules.night) {
      const { tag, boost } = rules.night;
      const isNightTrack = (item: RadioItem): boolean => item.tags?.includes(tag) ?? false;
      if (isNightTime()) {
        weightOf = (item) => (item.weight ?? 1) * (isNightTrack(item) ? boost : 1);
      } else {
        // W dzień gramy muzykę nocną tylko wtedy, gdy nie ma nic innego.
        const daytime = pool.filter((item) => !isNightTrack(item));
        if (daytime.length > 0) pool = daytime;
      }
    }

    const item = pickFrom(pool, weightOf);
    if (item.type === 'vocal' && rules.vocalEvery) {
      tracksSinceVocal = 0;
      vocalAfter = pickInRange(rules.vocalEvery);
    } else {
      tracksSinceVocal += 1;
    }
    tracksSinceJingle += 1;
    lastArtist = item.artist;
    return item;
  }

  function pickNext(): RadioItem | null {
    const breakItem = pickBreak();
    if (breakItem) {
      lastWasBreak = true;
      return breakItem;
    }
    lastWasBreak = false;
    return pickTrack();
  }

  /** Komunikat pełnej godziny, jeśli jest należny i mamy jego nagranie. */
  function takeTimeAnnouncement(): RadioItem | null {
    const due = scheduler?.getDueAnnouncement();
    if (!due) return null;

    // Brak pliku też zamyka sprawę tej godziny - nie sprawdzamy w kółko.
    scheduler?.markAnnounced();
    return timeItems.get(getTimeItemId(due.hour)) ?? null;
  }

  let current = pickNext();
  let next = current ? pickNext() : null;

  return {
    get current() {
      return current;
    },
    get next() {
      return next;
    },
    advance() {
      const announcement = takeTimeAnnouncement();
      if (announcement) {
        // Komunikat wchodzi przed zaplanowaną pozycją; `next` zostaje bez zmian.
        current = announcement;
        return current;
      }
      current = next;
      next = current ? pickNext() : null;
      return current;
    },
  };
}
